import {
    Flex,
    Text,
    Spinner,
    Divider,
    CardHeader,
    Card,
    CardBody,
    SimpleGrid,
    Select,
    CardFooter,
    Spacer,
    Button,
    Heading,
    useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState, useMemo, useRef } from 'react'
import QueueCard from './QueueCard'
import {
    CheckoutsType,
    useGetCheckoutQueue
} from '@/modules/teacher/hooks/useCheckoutQueue'
import DebouncedInput from '@/components/DebouncedInput'
import { useClearQueue } from '@/modules/teacher/hooks/useClearQueue'
import PermissionGate from '@/shared/components/PermissionGate'
import { UserTypes } from '@/shared/types/auth'
import ClearQueueDialog from './ClearQueueDialog'

interface QueueTabProps {
    branchId: string
}

export default function QueueTab({ branchId }: QueueTabProps) {
    const [ageGroup, setAgeGroup] = useState('')
    const [searchText, setSearchText] = useState('')
    const { loading, data } = useGetCheckoutQueue(branchId, ageGroup)
    const [checkoutsInAgeGroup, setCheckoutsInAgeGroup] = useState<CheckoutsType[]>([])

    const handleAgeGrpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setAgeGroup(e.target.value)
    }

    const { isOpen, onOpen, onClose } = useDisclosure()
    const cancelRef = useRef<HTMLButtonElement>(null)

    const { clearQueue, clearing } = useClearQueue()

    const handleClearQueue = async () => {
        if (!branchId) return
        await clearQueue(branchId)
        onClose()
    }

    // Filters checkouts based on search text
    const searchFilteredCheckouts = useMemo(() => {
        if (!searchText) return checkoutsInAgeGroup

        return checkoutsInAgeGroup.filter((checkout) =>
            [
                checkout.child?.firstName,
                checkout.child?.lastName,
                checkout.child?.ageGroup
            ].some((field) => field?.toLowerCase().includes(searchText.toLowerCase()))
        )
    }, [searchText, checkoutsInAgeGroup])

    useEffect(() => {
        // Filters checkouts based on selected age group
        const filterCheckouts = () => {
            if (!data?.getCheckoutQueue) return []

            const ageGroupFiltered = ageGroup
                ? data.getCheckoutQueue.filter(
                      (checkout: CheckoutsType) =>
                          checkout.child?.ageGroup?.toLowerCase() ===
                          ageGroup.toLowerCase()
                  )
                : data.getCheckoutQueue

            // Deduplicate checkouts based on child?._id
            const uniqueCheckouts = new Map<string, CheckoutsType>()
            ageGroupFiltered.forEach((checkout: CheckoutsType) => {
                if (checkout.child && !uniqueCheckouts.has(checkout.child._id)) {
                    uniqueCheckouts.set(checkout.child._id, checkout)
                }
            })

            return Array.from(uniqueCheckouts.values())
        }

        setCheckoutsInAgeGroup(filterCheckouts())
    }, [ageGroup, data?.getCheckoutQueue])

    return (
        <>
            <Flex my={4} maxW="sm" gap={6} alignItems="center">
                <Text>Filter by</Text>
                <Spacer />
                <Select
                    minW="240px"
                    placeholder="Age Group"
                    width={'auto'}
                    bg="white"
                    borderColor="gray.300"
                    color="brand"
                    value={ageGroup}
                    onChange={handleAgeGrpChange}
                >
                    <option value="Toddlers">Toddlers</option>
                    <option value="PreSchool">Pre School</option>
                    <option value="SchoolAge">School Age</option>
                    <option value="Teenagers">Teenagers</option>
                    <option value="Samuels">Samuels</option>
                    <option value="Adult">Adult</option>
                </Select>
            </Flex>
            <Card
                maxW="sm"
                bg={'brand.700'}
                color={'white'}
                align="center"
                variant={'elevated'}
            >
                <CardHeader>
                    <Heading size="lg">Total</Heading>
                </CardHeader>
                <CardBody>
                    <Heading textAlign="center" fontSize="6xl">
                        {loading ? <Spinner /> : checkoutsInAgeGroup.length || 0}
                    </Heading>
                </CardBody>
                <CardFooter>
                    <Text>Children waiting to be taken out</Text>
                </CardFooter>
            </Card>
            <PermissionGate
                allowedTypes={[UserTypes.IS_ADMIN_TEACHER, UserTypes.IS_GLOBAL_ADMIN]}
            >
                <Button
                    size="lg"
                    w={'sm'}
                    mt={4}
                    variant="outline"
                    colorScheme="red"
                    onClick={onOpen}
                    isDisabled={checkoutsInAgeGroup.length === 0}
                >
                    Clear all children
                </Button>
            </PermissionGate>

            <Divider my={4} maxW="sm" />

            <DebouncedInput
                value={searchText ?? ''}
                onChange={(value) => setSearchText(String(value))}
                placeholder="Search queue..."
            />

            <SimpleGrid
                spacing={3}
                templateColumns="repeat(auto-fill, minmax(300px, 1fr))"
            >
                {searchFilteredCheckouts &&
                    searchFilteredCheckouts.map((checkouts) => (
                        <QueueCard key={checkouts._id} details={checkouts} />
                    ))}
            </SimpleGrid>

            <ClearQueueDialog
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleClearQueue}
                isLoading={clearing}
                cancelRef={cancelRef}
            />
        </>
    )
}
